import type { ProcedureTaskCloseResult, ProcedureTaskDto, ProcedureTaskMeasure } from '@bk/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, errorMessage } from '../../lib/api';
import { dateOnly } from '../../lib/format';
import { useTenant } from '../../lib/useTenant';
import { Badge, Button, ErrorText, Select } from '../../ui';
import { ExecutorTaskPanel } from './Executions';
import { WorkClaimControls } from './WorkClaimControls';

const ORIGIN: Record<ProcedureTaskDto['origine'], string> = { tempo: 'dal tempo', esterno: 'da un evento', dopo: 'da un’altra procedura', controllo: 'da un controllo fuori standard' };

/** Il valore atteso di uno standard in parole: «al massimo 12 giorni», «da fissare», «dal contratto». */
export function standardValue(s: ProcedureTaskDto['standard'][number]): string {
  if (s.base === 'contratto') return `dal contratto${s.da ? ` (${s.da.campo})` : ''}`;
  if (s.valore === null) return 'da fissare';
  const cmp = s.confronto === 'al_massimo' ? 'al massimo ' : s.confronto === 'almeno' ? 'almeno ' : s.confronto === 'entro' ? 'entro ' : '';
  return `${cmp}${s.valore}${s.unita ? ` ${s.unita}` : ''}`;
}

/**
 * Un compito da una procedura attiva (ADR 0215 tappa 4): da cosa è partito, la scadenza, chi lo svolge e chi approva, i
 * programmi che lo possono svolgere. Si chiude con la nota e, per un controllo, con il valore misurato confrontato con lo
 * standard; un compito partito da un'altra procedura si annulla se la sua condizione non vale.
 */
export function ProcedureTaskCard({ task, showProcedure = true }: { task: ProcedureTaskDto; showProcedure?: boolean }) {
  const { tenant, t } = useTenant();
  const qc = useQueryClient();
  const [closing, setClosing] = useState(false);
  const [stepsReady, setStepsReady] = useState(true);
  const [canWork, setCanWork] = useState(false);
  const [note, setNote] = useState('');
  const [measured, setMeasured] = useState('');
  const [expected, setExpected] = useState('');
  const [outcome, setOutcome] = useState<'' | 'in_standard' | 'fuori_standard'>('');
  const [stdKey, setStdKey] = useState(task.standard[0]?.chiave ?? '');
  const [result, setResult] = useState<ProcedureTaskCloseResult | null>(null);
  const refresh = () => void qc.invalidateQueries({ queryKey: ['tenant', tenant.id] });
  const std = task.standard.find((s) => s.chiave === stdKey) ?? null;
  const num = (v: string) => (v.trim() && !Number.isNaN(Number(v.replace(',', '.'))) ? Number(v.replace(',', '.')) : v.trim() || null);
  const close = useMutation({
    mutationFn: () =>
      api<ProcedureTaskCloseResult>(t(`/procedure-tasks/${task.id}/close`), {
        body: {
          ...(note.trim() ? { nota: note.trim() } : {}),
          ...(std ? { standard: std.chiave, valore_misurato: num(measured) } : {}),
          ...(std?.base === 'contratto' && expected.trim() ? { valore_atteso: Number(expected.replace(',', '.')) } : {}),
          ...(outcome ? { esito: outcome } : {}),
        },
      }),
    onSuccess: (r) => {
      setResult(r);
      refresh();
    },
  });
  // La misura dai dati (ADR 0215 tappa 5): se dà l'esito, il compito si chiude con quella senza scrivere il valore.
  const measure = useMutation({ mutationFn: () => api<ProcedureTaskMeasure>(t(`/procedure-tasks/${task.id}/measure?standard=${encodeURIComponent(stdKey)}`)) });
  const m = measure.data?.standard === stdKey ? measure.data : null;
  const cancel = useMutation({ mutationFn: () => api(t(`/procedure-tasks/${task.id}/cancel`), { body: note.trim() ? { nota: note.trim() } : {} }), onSuccess: refresh });
  const error = close.error ?? cancel.error ?? measure.error;
  const input = 'w-full rounded-lg border border-black/15 px-3 py-1.5 text-sm outline-none focus:border-accent';

  if (result)
    return (
      <div className="rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-900">
        Chiuso{result.compito.esito === 'fuori_standard' ? ' fuori standard' : result.compito.esito === 'in_standard' ? ' in standard' : ''}: {task.procedura}.
        {result.aperti.length > 0 && <> Aperti dopo: {result.aperti.map((a) => a.procedura).join(', ')}.</>}
      </div>
    );

  return (
    <article className="rounded-lg border border-black/10 bg-white px-3 py-2.5">
      <div className="flex flex-wrap items-center gap-2">
        {showProcedure && <span className="text-sm font-medium">{task.procedura}</span>}
        <span className="text-xs text-ink/55">
          {task.trigger}
          {task.periodo && <> · periodo {dateOnly(task.periodo.inizio)}–{dateOnly(task.periodo.fine)}</>}
        </span>
        {task.scadenza && <Badge tone={task.scaduto ? 'red' : 'gray'}>entro il {dateOnly(task.scadenza)}</Badge>}
        <Badge tone="blue">{ORIGIN[task.origine]}</Badge>
      </div>
      <p className="mt-1 text-xs text-ink/60">
        {task.sede && <>{task.sede} · </>}
        La svolge: {task.ruolo.nome} ({task.da_assegnare ? <span className="text-amber-700">da assegnare</span> : task.persone.join(', ')})
        {task.approva && <> · approva: {task.approva}</>}
        {task.entita && <> · per {task.dato ? `${task.entita}: ${task.dato}` : `ogni ${task.entita}`}</>}
      </p>
      {task.se && <p className="mt-1 text-xs text-amber-800">Vale {task.se.replace(/^se /, 'se ')}: se non vale, annullalo.</p>}
      {task.nota_apertura && <p className="mt-1 text-xs text-ink/60">{task.nota_apertura}</p>}
      {task.lo_puo_svolgere.length > 0 && <p className="mt-1 text-xs text-ink/60">Lo può svolgere: {task.lo_puo_svolgere.join(', ')}</p>}
      <p className="mt-1 text-xs text-ink/60">Prova: {task.prova_attesa}</p>
      {task.standard.length > 0 && <p className="mt-1 text-xs text-ink/60">Standard: {task.standard.map((s) => `${s.nome} (${standardValue(s)})`).join('; ')}</p>}
      {task.stato === 'aperta' && <WorkClaimControls family="procedure_task" taskId={task.id} canClaim={task.puo_chiudere} onCanWorkChange={setCanWork} />}

      <ExecutorTaskPanel taskId={task.id} onCanClose={setStepsReady} />
      {task.puo_chiudere && stepsReady && !closing && (
        <div className="mt-2 flex gap-2">
          <Button variant="ghost" className="px-2 py-1 text-xs" disabled={!canWork} onClick={() => setClosing(true)}>
            Chiudi
          </Button>
          {(task.se || task.origine !== 'tempo') && (
            <button type="button" className="text-xs text-ink/50 hover:text-ink" disabled={!canWork || cancel.isPending} onClick={() => cancel.mutate()}>
              Non vale, annulla
            </button>
          )}
        </div>
      )}
      {closing && (
        <div className="mt-2 space-y-2">
          {task.standard.length > 0 && (
            <>
              {task.standard.length > 1 && (
                <Select value={stdKey} onChange={(e) => setStdKey(e.target.value)}>
                  {task.standard.map((s) => (
                    <option key={s.chiave} value={s.chiave}>
                      {s.nome}
                    </option>
                  ))}
                </Select>
              )}
              {std && (
                <p className="text-xs text-ink/60">
                  {std.misura} · atteso: {standardValue(std)}
                </p>
              )}
              {std?.dai_dati?.come && (
                <div className="rounded-lg bg-black/[0.03] px-2 py-1.5 text-xs text-ink/70">
                  <button type="button" className="font-medium text-accent hover:underline" disabled={measure.isPending} onClick={() => measure.mutate()}>
                    {measure.isPending ? 'Misuro…' : 'Misura dai dati'}
                  </button>
                  {m && (
                    <p className="mt-1">
                      {m.esito ? (
                        <>
                          <span className={m.esito === 'fuori_standard' ? 'font-medium text-red-700' : 'font-medium text-green-800'}>{m.esito === 'fuori_standard' ? 'Fuori standard' : 'In standard'}</span>
                          {m.voci ? `: ${m.voci.fuori} su ${m.voci.controllate} voci fuori standard` : `: ${m.valore}`}
                          {m.voci && m.voci.non_verificabili > 0 && ` (${m.voci.non_verificabili} senza i dati per dirlo)`}
                          {m.voci && m.voci.esempi.length > 0 && <> · per esempio {m.voci.esempi.map((e) => e.rif).join(', ')}</>}. Chiudendo senza un valore, vale questa misura.
                        </>
                      ) : (
                        <>Dai dati non si misura: {m.manca}.</>
                      )}
                    </p>
                  )}
                  {m?.spiegazione && <p className="mt-0.5 text-ink/50">Conta: {m.spiegazione}</p>}
                </div>
              )}
              {std?.dai_dati?.manca && <p className="text-xs text-ink/50">Dai dati non si misura ancora: {std.dai_dati.manca}.</p>}
              <input className={input} placeholder={`Valore misurato${std?.unita ? ` (${std.unita})` : ''}`} value={measured} onChange={(e) => setMeasured(e.target.value)} />
              {std?.base === 'contratto' && <input className={input} placeholder="Valore promesso nel contratto" value={expected} onChange={(e) => setExpected(e.target.value)} />}
              <Select value={outcome} onChange={(e) => setOutcome(e.target.value as typeof outcome)}>
                <option value="">Esito dal confronto con lo standard</option>
                <option value="in_standard">In standard</option>
                <option value="fuori_standard">Fuori standard</option>
              </Select>
            </>
          )}
          <input className={input} placeholder="Nota facoltativa" value={note} onChange={(e) => setNote(e.target.value)} />
          <div className="flex gap-2">
            <Button className="px-3 py-1 text-xs" disabled={!canWork || close.isPending} onClick={() => close.mutate()}>
              Chiudi il compito
            </Button>
            <button type="button" className="text-xs text-ink/50 hover:text-ink" onClick={() => setClosing(false)}>
              Lascia stare
            </button>
          </div>
        </div>
      )}
      {error && <ErrorText>{errorMessage(error)}</ErrorText>}
    </article>
  );
}
